import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ContextTabs, ViewToggle, RecordCard, CompactRow, DataTable, ServiceHealthRail, StatusBadge,
  DetailDrawer, ActionBar, MobileFilterDrawer, LoadingState, EmptyState, ErrorState, MessageComposer,
} from './osPrimitives';
import { Search, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const td = () => new Date().toISOString().slice(0, 10);
const money = (n) => n === undefined || n === null ? '—' : `$${Number(n).toLocaleString()}`;

async function getJSON(path) {
  const token = localStorage.getItem('ace_admin_token');
  const r = await fetch(`${API}${path}`, { credentials: 'include', headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!r.ok) throw new Error(`${path} ${r.status}`);
  return r.json();
}

async function postJSON(path, body) {
  const token = localStorage.getItem('ace_admin_token');
  const r = await fetch(`${API}${path}`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body || {}),
  });
  if (!r.ok) throw new Error(`${path} ${r.status}`);
  return r.json();
}

const TABS = [
  { id: 'leads', label: 'Leads' }, { id: 'quotes', label: 'Quotes' }, { id: 'booked', label: 'Booked' },
  { id: 'today', label: 'Today' }, { id: 'upcoming', label: 'Upcoming' }, { id: 'active', label: 'Active' },
  { id: 'recovery', label: 'Recovery' }, { id: 'overdue', label: 'Overdue' }, { id: 'completed', label: 'Completed' },
];
const TERMINAL = ['completed', 'cancelled', 'deleted', 'closed', 'finalized'];
const LEVEL_STATUS = { danger: 'risk', warning: 'attention', info: 'waiting' };

// Presentation adapter (UI only — no new business object). Normalizes canonical records for display.
function adaptBooking(b) {
  const driverKnown = 'driver_id' in b;
  const driverAssigned = b.driver_id && !['', 'unassigned', null].includes(b.driver_id);
  return {
    kind: 'BK', id: b.booking_id, client: b.client_name || 'Client', serviceType: b.service_type || 'Service',
    date: (b.event_date || '').slice(0, 10), time: b.pickup_time || '', vehicle: b.vehicle || '',
    total: b.total, state: b.cc_status || 'upcoming', raw: b,
    health: [
      { key: 'booking', label: 'Booking', status: b.cc_status ? 'ready' : 'unknown' },
      { key: 'payment', label: 'Payment', status: b.payment_status ? (b.payment_status === 'paid' ? 'ready' : 'attention') : 'unknown' },
      { key: 'vehicle', label: 'Vehicle', status: b.vehicle ? 'ready' : 'unknown' },
      { key: 'driver', label: 'Driver', status: driverKnown ? (driverAssigned ? 'ready' : 'attention') : 'unknown' },
      { key: 'client', label: 'Client', status: b.client_name ? 'ready' : 'unknown' },
      { key: 'fulfillment', label: 'Fulfillment', status: b.is_farmout ? (b.farmout_confirmation ? 'ready' : 'attention') : 'ready' },
    ],
  };
}

export default function ServicesLive() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return TABS.some((x) => x.id === t) ? t : 'today';
  });
  const [view, setView] = useState('cards');
  const [q, setQ] = useState('');
  const [peek, setPeek] = useState(null);
  const [peekTab, setPeekTab] = useState('service');
  const [filterOpen, setFilterOpen] = useState(false);
  const [data, setData] = useState({ loading: true });
  const [composer, setComposer] = useState(false);
  const [converting, setConverting] = useState(false);

  const convertQuote = async () => {
    const qid = peek?.id;
    if (!qid || converting) return;
    setConverting(true);
    try {
      const r = await postJSON(`/api/command-center/quote-log/${qid}/convert`, {});
      if (r.booking_id) {
        toast.success(r.already_converted ? 'Already converted' : 'Quote converted to booking');
        setPeek(null);
        navigate(`/os/services/${r.booking_id}`);
      } else { toast.error('Convert failed'); }
    } catch (e) { toast.error(e.message || 'Convert failed'); }
    finally { setConverting(false); }
  };

  const sendQuoteSMS = async (text) => {
    const to = peek?.raw?.client_phone || peek?.raw?.phone;
    const r = await postJSON('/api/command-center/quo/send-sms', { to, message: text });
    r.success ? toast.success('SMS sent') : toast.error('SMS failed');
  };

  const load = async () => {
    setData({ loading: true });
    const out = {};
    const safe = async (k, p) => { try { out[k] = await getJSON(p); } catch { out[k + 'Err'] = true; } };
    await Promise.all([
      safe('inq', '/api/ops/inquiries'),
      safe('quotes', '/api/command-center/quote-log'),
      safe('bookings', '/api/command-center/confirmed-bookings'),
      safe('alerts', '/api/command-center/alerts'),
    ]);
    setData({ loading: false, ...out });
  };
  useEffect(() => { load(); }, []);

  const derived = useMemo(() => {
    const day = td();
    const inq = (data.inq?.inquiries) || [];
    const quotes = (data.quotes?.quotes) || [];
    const bookings = ((data.bookings?.bookings) || []).map(adaptBooking);
    const alerts = (data.alerts?.alerts) || [];
    return {
      leads: inq,
      quotes: quotes.filter((qt) => !qt.booking_id && !['confirmed', 'converted', 'booked'].includes((qt.status || '').toLowerCase())),
      booked: bookings,
      today: bookings.filter((b) => b.date === day),
      upcoming: bookings.filter((b) => b.date > day),
      active: bookings.filter((b) => b.date === day && b.state !== 'completed'),
      recovery: alerts,
      overdue: bookings.filter((b) => b.date && b.date < day && !TERMINAL.includes((b.state || '').toLowerCase())),
      completed: bookings.filter((b) => b.state === 'completed'),
    };
  }, [data]);

  const counts = Object.fromEntries(TABS.map((t) => [t.id, (derived[t.id] || []).length]));
  const err = data[{ leads: 'inq', quotes: 'quotes', booked: 'bookings', today: 'bookings', upcoming: 'bookings', active: 'bookings', recovery: 'alerts', overdue: 'bookings', completed: 'bookings' }[tab] + 'Err'];

  const filterText = (s) => !q || (s || '').toLowerCase().includes(q.toLowerCase());

  return (
    <div className="os-animate" data-testid="services-live">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <ContextTabs tabs={TABS.map((t) => ({ ...t, count: counts[t.id] }))} active={tab} onChange={setTab} testid="services-tabs" />
        <div className="flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-2 os-input" style={{ minHeight: 40, width: 220 }}>
            <Search className="h-4 w-4" style={{ color: 'var(--os-muted)' }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter…" data-testid="services-search"
                   className="bg-transparent outline-none text-sm flex-1" style={{ color: 'var(--os-text)' }} />
          </div>
          <button className="os-btn-ghost sm:hidden" style={{ padding: '8px 12px', minHeight: 40 }} onClick={() => setFilterOpen(true)} data-testid="services-filter-btn"><SlidersHorizontal className="h-4 w-4" /></button>
          {tab === 'quotes' && <button className="os-btn-primary" style={{ minHeight: 40 }} onClick={() => navigate('/os/services/quotes/new')} data-testid="services-new-quote">New Quote</button>}
          <ViewToggle view={view} onChange={setView} testid="services-view" />
        </div>
      </div>

      {tab === 'overdue' && !data.loading && <p className="text-[11px] mb-2" data-testid="overdue-note" style={{ color: 'var(--os-warning)' }}>Service date passed and the record is still open. This is a view classification only — the canonical booking status is unchanged.</p>}
      {data.loading ? <LoadingState rows={4} />
        : err ? <ErrorState body="This view's data source is unavailable." onRetry={load} />
        : <Body tab={tab} rows={derived[tab] || []} view={view} filterText={filterText}
                onPeek={(b) => { setPeek(b); setPeekTab('service'); }} navigate={navigate} />}

      {/* Booking Record Peek (BK) */}
      {peek && peek.kind === 'BK' && <DetailDrawer open title={`${peek.serviceType}`} subtitle={`${peek.client} · ${peek.id}`}
        tabs={[{ id: 'service', label: 'Service' }, { id: 'client', label: 'Client' }, { id: 'vehicle', label: 'Vehicle' },
               { id: 'driver', label: 'Driver' }, { id: 'money', label: 'Money' }, { id: 'fulfillment', label: 'Fulfillment' }]}
        activeTab={peekTab} onTab={setPeekTab} onClose={() => setPeek(null)}
        footer={<ActionBar primary={{ label: 'Open Service Record', onClick: () => navigate(`/os/services/${peek.id}`) }}
                          secondary={[{ label: 'Edit Service', onClick: () => navigate(`/os/services/${peek.id}/edit`) },
                                      { label: 'Client 360 →', onClick: () => navigate(`/os/clients/${encodeURIComponent(peek?.client || '')}`) },
                                      { label: 'Fulfillment →', onClick: () => navigate(`/os/network?net=fulfillment&bk=${peek?.id || ''}`) }]} />}>
        <PeekBody peek={peek} tab={peekTab} />
      </DetailDrawer>}

      {/* Lead / Quote Peek (INQ / QT) */}
      {peek && (peek.kind === 'INQ' || peek.kind === 'QT') && <DetailDrawer open
        title={peek.kind === 'INQ' ? 'Lead' : 'Quote'} subtitle={`${peek.client || '—'} · ${peek.id || ''}`}
        onClose={() => setPeek(null)}
        footer={<ActionBar
          primary={peek.kind === 'QT' ? quotePrimary(peek, navigate, convertQuote, converting) : leadQuotePrimary(peek, navigate)}
          secondary={peek.kind === 'QT' ? quoteSecondary(peek, navigate, () => setComposer(true)) : leadQuoteSecondary(peek, navigate)} />}>
        <LeadQuoteBody peek={peek} />
      </DetailDrawer>}

      <MessageComposer open={composer} onClose={() => setComposer(false)}
        to={peek?.raw?.client_phone || peek?.raw?.phone} emailVerified={false}
        onSendSMS={sendQuoteSMS} testid="quote-message-composer" />

      <MobileFilterDrawer open={filterOpen} onClose={() => setFilterOpen(false)} onApply={() => setFilterOpen(false)}>
        <div className="os-input flex items-center gap-2"><Search className="h-4 w-4" style={{ color: 'var(--os-muted)' }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Client / service…" data-testid="services-search-mobile"
                 className="bg-transparent outline-none text-sm flex-1" style={{ color: 'var(--os-text)' }} /></div>
      </MobileFilterDrawer>
    </div>
  );
}

function Body({ tab, rows, view, filterText, onPeek, navigate }) {
  const shown = rows.filter((r) => {
    if (tab === 'leads') return filterText(r.client_name) || filterText(r.service_type);
    if (tab === 'quotes') return filterText(r.client_name) || filterText(r.quote_id);
    if (tab === 'recovery') return filterText(r.title);
    return filterText(r.client) || filterText(r.serviceType) || filterText(r.id);
  });
  if (!shown.length) return <EmptyState title="Nothing here right now" body={EMPTY[tab]} />;

  if (tab === 'leads') return <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="services-list">
    {shown.map((l) => <RecordCard key={l.inquiry_id} kind="Lead · INQ" title={l.client_name || 'Inquiry'} subtitle={l.service_type || l.message}
      status={l.status === 'new' ? 'attention' : 'waiting'} meta={chain(l)} onOpen={() => onPeek({ kind: 'INQ', id: l.inquiry_id, client: l.client_name, raw: l })} testid={`svc-lead-${l.inquiry_id}`} />)}
  </div>;

  if (tab === 'quotes') return <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="services-list">
    {shown.map((qt) => <RecordCard key={qt.quote_id} kind={`Quote · ${qt.quote_id}`} title={qt.client_name || 'Quote'} subtitle={money(qt.quoted_amount ?? qt.total)}
      status={qt.status === 'accepted' ? 'ready' : qt.follow_up_date ? 'attention' : 'waiting'}
      meta={[{ label: 'Amount', value: money(qt.quoted_amount ?? qt.total) }, { label: 'Follow-up', value: qt.follow_up_date || '—' }]}
      onOpen={() => onPeek({ kind: 'QT', id: qt.quote_id, client: qt.client_name, raw: qt })} testid={`svc-quote-${qt.quote_id}`} />)}
  </div>;

  if (tab === 'recovery') return <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="services-list">
    {shown.map((a, i) => <div key={i} className="os-card p-4" style={{ borderLeft: `3px solid var(--os-danger)` }} data-testid={`svc-recovery-${i}`}>
      <div className="flex items-center justify-between"><StatusBadge status={LEVEL_STATUS[a.level] || 'risk'} />{a.kind && <span className="text-[10px]" style={{ color: 'var(--os-muted)' }}>{a.kind}</span>}</div>
      <h3 className="font-bold mt-2" style={{ color: 'var(--os-text)' }}>{a.title}</h3>
      <p className="text-xs mt-1" style={{ color: 'var(--os-muted)' }}>{a.detail}</p>
      {a.link && <button className="os-btn-primary mt-3" style={{ minHeight: 38, padding: '8px 14px' }} onClick={() => navigate(a.link)} data-testid={`svc-recovery-open-${i}`}>Open Recovery</button>}
    </div>)}
  </div>;

  // bookings (booked/today/upcoming/active/completed)
  if (view === 'table') return <div data-testid="services-list"><DataTable testid="data-table"
    columns={[{ key: 'id', label: 'BK' }, { key: 'client', label: 'Client' }, { key: 'serviceType', label: 'Service' },
              { key: 'date', label: 'Date' }, { key: 'total', label: 'Total', render: (r) => money(r.total) },
              { key: 'state', label: 'Status', render: (r) => <StatusBadge status={r.state === 'completed' ? 'ready' : 'waiting'} label={r.state} /> }]}
    rows={shown} onRow={onPeek} /></div>;

  if (view === 'compact') return <div data-testid="services-list">{shown.map((b) => <CompactRow key={b.id} title={`${b.client} · ${b.serviceType}`}
    subtitle={`${b.date} ${b.time} · ${b.vehicle || '—'}`} status={b.state === 'completed' ? 'ready' : 'waiting'} right={money(b.total)} onOpen={() => onPeek(b)} />)}</div>;

  return <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="services-list">
    {shown.map((b) => (
      <div key={b.id} className="os-card p-4 cursor-pointer" onClick={() => onPeek(b)} data-testid={`svc-booking-${b.id}`}>
        <div className="flex items-start justify-between">
          <div className="min-w-0"><p className="os-chip" style={{ color: 'var(--os-muted)', border: '1px solid var(--os-border)' }}>{b.serviceType}</p>
            <h3 className="font-bold text-base mt-1 truncate" style={{ color: 'var(--os-text)' }}>{b.client}</h3>
            <p className="text-xs mt-0.5" style={{ color: 'var(--os-muted)' }}>{b.date} {b.time} · {b.vehicle || '—'}</p></div>
          <StatusBadge status={b.state === 'completed' ? 'ready' : 'waiting'} label={b.state} />
        </div>
        <div className="mt-3"><ServiceHealthRail items={b.health} testid={`svc-health-${b.id}`} /></div>
        <p className="text-xs mt-2 font-mono" style={{ color: 'var(--os-muted)' }}>{b.id} · {money(b.total)}</p>
      </div>
    ))}
  </div>;
}

const EMPTY = {
  leads: 'No open inquiries.', quotes: 'No active quotes.', booked: 'No confirmed bookings.',
  today: 'No services scheduled today.', upcoming: 'No upcoming services.', active: 'No services are active right now.',
  recovery: 'No service issues need recovery. This is a positive operational state.', completed: 'No completed services yet.',
};
const chain = (l) => {
  const c = [];
  if (l.inquiry_id) c.push({ label: 'Inquiry', value: l.inquiry_id });
  if (l.quote_id) c.push({ label: 'Quote', value: l.quote_id });
  if (l.booking_id) c.push({ label: 'Booking', value: l.booking_id });
  return c.length ? c : [{ label: 'Status', value: l.status || '—' }];
};

function PeekBody({ peek, tab }) {
  const b = peek.raw || {};
  const F = ({ label, value }) => <div className="mb-3"><p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--os-muted)' }}>{label}</p><p className="text-sm" style={{ color: 'var(--os-text)' }}>{value ?? '—'}</p></div>;
  if (tab === 'service') return <div><div className="flex gap-2 mb-3"><StatusBadge status={peek.state === 'completed' ? 'ready' : 'waiting'} label={peek.state} /></div>
    <ServiceHealthRail items={peek.health} /><div className="mt-4"><F label="Service type" value={peek.serviceType} /><F label="Date / time" value={`${peek.date} ${peek.time}`} /><F label="Passengers" value={b.passengers} /></div></div>;
  if (tab === 'client') return <div><F label="Client" value={peek.client} /><F label="Phone" value={b.client_phone} /><F label="Email" value={b.client_email} /></div>;
  if (tab === 'vehicle') return <div><F label="Vehicle / class" value={peek.vehicle} /></div>;
  if (tab === 'driver') return <div><F label="Assigned driver" value={b.driver_id && b.driver_id !== 'unassigned' ? b.driver_id : (('driver_id' in b) ? 'Unassigned' : undefined)} /></div>;
  if (tab === 'money') return <div><F label="Total" value={money(peek.total)} /><F label="Payment state" value={b.payment_status} /></div>;
  if (tab === 'fulfillment') return <div>
    <F label="Mode" value={b.is_farmout ? 'Affiliate / Collective' : 'ACE Direct'} />
    {b.is_farmout && <><F label="Awarded partner" value={b.farmout_partner} /><F label="Confirmation" value={b.farmout_confirmation ? 'Confirmed' : 'Pending'} /></>}
  </div>;
  return null;
}

function quotePrimary(peek, navigate, convertQuote, converting) {
  const r = peek.raw || {};
  if (r.booking_id || r.converted_booking_id) return { label: 'Open Service', onClick: () => navigate(`/os/services/${r.booking_id || r.converted_booking_id}`) };
  if (['confirmed', 'converted', 'deleted'].includes(r.status)) return { label: `Quote ${r.status}`, onClick: () => {} };
  return { label: converting ? 'Converting…' : 'Convert to Booking', onClick: convertQuote };
}
function quoteSecondary(peek, navigate, openComposer) {
  const r = peek.raw || {};
  const converted = r.booking_id || r.converted_booking_id || ['confirmed', 'converted'].includes((r.status || '').toLowerCase());
  const out = [{ label: 'Open Quote Record →', onClick: () => navigate(`/os/services/quotes/${peek.id}`) }];
  if (!converted) out.push({ label: 'Edit Quote →', onClick: () => navigate(`/os/services/quotes/${peek.id}/edit`) });
  if (r.client_phone || r.phone) out.push({ label: 'Send via SMS', onClick: openComposer });
  out.push({ label: 'Client 360 →', onClick: () => navigate(`/os/clients/${encodeURIComponent(peek.client || '')}`) });
  if (r.booking_id || r.converted_booking_id) out.push({ label: 'Service Record →', onClick: () => navigate(`/os/services/${r.booking_id || r.converted_booking_id}`) });
  return out;
}

function leadQuoteChain(r) {
  const params = new URLSearchParams();
  if (r.inquiry_id) params.set('inquiry_id', r.inquiry_id);
  if (r.client_name) params.set('client_name', r.client_name);
  if (r.client_phone || r.phone) params.set('client_phone', r.client_phone || r.phone);
  if (r.client_email || r.email) params.set('client_email', r.client_email || r.email);
  if (r.service_type) params.set('service_type', r.service_type);
  if (r.event_date) params.set('event_date', (r.event_date || '').slice(0, 10));
  return params.toString();
}
function leadQuotePrimary(peek, navigate) {
  const r = peek.raw || {};
  if (r.quote_id) return { label: 'Open Quote', onClick: () => navigate(`/os/services/quotes/${r.quote_id}`) };
  return { label: 'Create Quote', onClick: () => navigate(`/os/services/quotes/new?${leadQuoteChain(r)}`) };
}
function leadQuoteSecondary(peek, navigate) {
  const r = peek.raw || {};
  const out = [{ label: 'Client 360 →', onClick: () => navigate(`/os/clients/${encodeURIComponent(peek.client || '')}`) }];
  if (peek.kind === 'INQ') {
    if (!r.quote_id) out.push({ label: 'Create Quote', onClick: () => navigate(`/os/services/quotes/new?${leadQuoteChain(r)}`) });
    out.push({ label: 'New Service', onClick: () => navigate(`/os/services/new?${leadQuoteChain(r)}`) });
  }
  if (r.booking_id || r.converted_booking_id) out.push({ label: 'Service Record →', onClick: () => navigate(`/os/services/${r.booking_id || r.converted_booking_id}`) });
  return out;
}

function LeadQuoteBody({ peek }) {
  const r = peek.raw || {};
  const F = ({ label, value }) => <div className="mb-3" data-testid={`lq-${(label || '').toLowerCase().replace(/[^a-z]+/g, '-')}`}><p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--os-muted)' }}>{label}</p><p className="text-sm" style={{ color: 'var(--os-text)' }}>{value === undefined || value === null || value === '' ? '—' : value}</p></div>;
  const isQT = peek.kind === 'QT';
  return (
    <div data-testid={isQT ? 'quote-peek-body' : 'lead-peek-body'}>
      <div className="flex flex-wrap gap-2 mb-3">
        <StatusBadge status={r.status === 'accepted' || r.status === 'converted' ? 'ready' : (r.status === 'new' ? 'attention' : 'waiting')} label={r.status || (isQT ? 'quote' : 'lead')} />
        <span className="os-chip" style={{ color: 'var(--os-muted)', border: '1px solid var(--os-border)' }}>{isQT ? 'QT' : 'INQ'} · {peek.id}</span>
      </div>
      <F label="Client" value={peek.client} />
      <F label="Phone" value={r.client_phone || r.phone} />
      <F label="Email" value={r.client_email || r.email} />
      <F label="Service type" value={r.service_type || r.serviceType || r.service} />
      {isQT ? <F label="Quoted amount" value={money(r.quoted_amount ?? r.total)} /> : <F label="Request" value={r.message || r.notes} />}
      <F label="Created" value={(r.created_at || r.created || '').slice(0, 10)} />
      <F label="Status" value={r.status} />
      {r.inquiry_id && <F label="Linked inquiry" value={r.inquiry_id} />}
      {r.quote_id && !isQT && <F label="Linked quote" value={r.quote_id} />}
      {(r.booking_id || r.converted_booking_id) && <F label="Linked booking" value={r.booking_id || r.converted_booking_id} />}
      {r.follow_up_date && <F label="Follow-up" value={r.follow_up_date} />}
      {(r.notes || r.details) && <F label="Notes" value={r.notes || r.details} />}
    </div>
  );
}
